import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Camera, SpinnerGap, Sparkle, CheckCircle, ArrowRight,
  Leaf, Eye, WarningCircle, ShieldCheck
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { SEO } from '../components/SEO';
import { API_URL } from '../config/api';
import { AmazonDisclosure } from '../components/AmazonDisclosure';

const CONFIDENCE_COLOR = {
  high: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  low: 'bg-muted text-muted-foreground border-border',
};

const SEVERITY_COLOR = {
  low: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  moderate: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  severe: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

const TryDiagnosisPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [grassType, setGrassType] = useState('unknown');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = (ev) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_DIM = 1200;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          if (w > h) { h = Math.round(h * MAX_DIM / w); w = MAX_DIM; }
          else { w = Math.round(w * MAX_DIM / h); h = MAX_DIM; }
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressedUrl = canvas.toDataURL('image/jpeg', 0.7);
        setPhotoPreview(compressedUrl);
        setPhotoBase64(compressedUrl.split(',')[1]);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  const runDiagnosis = async () => {
    setErrorMsg('');
    if (!validEmail) { toast.error('Please enter a valid email'); return; }
    if (!photoBase64) { toast.error('Please upload a photo first'); return; }

    setDiagnosing(true);
    try {
      const res = await fetch(`${API_URL}/api/public/diagnose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          image_base64: photoBase64,
          grass_type: grassType,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Diagnosis failed' }));
        setErrorMsg(err.detail || 'Diagnosis failed');
        toast.error(err.detail || 'Diagnosis failed');
        return;
      }
      const data = await res.json();
      setDiagnosis(data);
      // Scroll to result
      setTimeout(() => {
        document.getElementById('diagnosis-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch {
      toast.error('Network error — try again');
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="try-diagnosis-page">
      <SEO
        title="Free AI Lawn Diagnosis — TurfPulse"
        description="Upload a photo of your lawn and get an instant AI diagnosis — identify weeds, disease, and pest issues in seconds. 1 free diagnosis, no credit card required."
      />

      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="try-logo-link">
            <TurfPulseLogoSVG size={28} />
            <span className="text-sm font-semibold tracking-tight">TurfPulse</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-xs" data-testid="try-login-link">
              Log in
            </Button>
            <Button size="sm" onClick={() => navigate('/')} className="text-xs" data-testid="try-signup-link">
              Sign up free
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkle size={12} weight="fill" /> Instant AI diagnosis — no sign-up
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            What's wrong with
            <br />
            <span className="text-primary">your lawn?</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            Snap a photo. See exactly what's hurting your turf — weeds, disease, pests, nutrient stress — plus the next step to fix it.
          </p>

          {/* Outcome-first trust row */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-6 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Answer in seconds</span>
            <span className="flex items-center gap-1"><ShieldCheck size={14} weight="fill" className="text-emerald-500" /> Your email stays private</span>
            <span className="flex items-center gap-1"><Leaf size={14} weight="fill" className="text-emerald-500" /> Backed by turf science</span>
          </div>
        </div>

        {/* Uploader Card */}
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="p-5 sm:p-7 space-y-5">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Your email</Label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!diagnosis}
                className="mt-1.5"
                data-testid="try-email-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">We'll send your diagnosis + seasonal lawn tips. Unsubscribe anytime.</p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Grass type (optional)</Label>
              <Select value={grassType} onValueChange={setGrassType} disabled={!!diagnosis}>
                <SelectTrigger className="mt-1.5" data-testid="try-grass-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">I'm not sure</SelectItem>
                  <SelectItem value="bermuda">Bermuda</SelectItem>
                  <SelectItem value="zoysia">Zoysia</SelectItem>
                  <SelectItem value="st-augustine">St. Augustine</SelectItem>
                  <SelectItem value="tall-fescue">Tall Fescue</SelectItem>
                  <SelectItem value="kentucky-bluegrass">Kentucky Bluegrass</SelectItem>
                  <SelectItem value="ryegrass">Ryegrass</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Lawn photo</Label>
              <label className={`mt-1.5 block cursor-pointer border-2 border-dashed rounded-lg transition-colors ${photoPreview ? 'border-primary/40' : 'border-border hover:border-primary/40'} ${diagnosis ? 'opacity-70 cursor-not-allowed' : ''}`}>
                <div className="p-5">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Your lawn" className="w-full max-h-80 object-contain rounded-md" data-testid="try-photo-preview" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground py-8">
                      <Camera size={36} weight="duotone" />
                      <span className="text-sm font-medium">Tap to upload photo</span>
                      <span className="text-xs">Works with phone camera roll or desktop</span>
                    </div>
                  )}
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={!!diagnosis} data-testid="try-photo-input" />
              </label>
            </div>

            <Button
              className="w-full h-12 text-sm font-semibold"
              onClick={runDiagnosis}
              disabled={diagnosing || !validEmail || !photoBase64 || !!diagnosis}
              data-testid="try-diagnose-btn"
            >
              {diagnosing ? (
                <><SpinnerGap size={18} className="mr-2 animate-spin" /> Analyzing lawn…</>
              ) : diagnosis ? (
                <><CheckCircle size={18} className="mr-2" /> Diagnosis complete</>
              ) : (
                <><Eye size={18} className="mr-2" /> Diagnose my lawn</>
              )}
            </Button>

            {errorMsg && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive" data-testid="try-error-msg">
                <WarningCircle size={16} className="shrink-0 mt-0.5" />
                <div>
                  <p>{errorMsg}</p>
                  {errorMsg.toLowerCase().includes('already') && (
                    <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-xs" onClick={() => navigate('/')} data-testid="try-login-cta">
                      Go to login →
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Diagnosis Result */}
        {diagnosis && (
          <div id="diagnosis-result" className="mt-8 space-y-5" data-testid="try-diagnosis-result">
            <Card className="border-2 border-primary/40 bg-primary/5">
              <CardContent className="p-5 sm:p-7 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Diagnosis</p>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground mt-0.5">{diagnosis.identification}</h2>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <span className={`text-[11px] px-2 py-1 rounded-full font-medium border ${CONFIDENCE_COLOR[diagnosis.confidence] || CONFIDENCE_COLOR.low}`}>
                      {diagnosis.confidence} confidence
                    </span>
                    <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${SEVERITY_COLOR[diagnosis.severity] || SEVERITY_COLOR.moderate}`}>
                      {diagnosis.severity} severity
                    </span>
                  </div>
                </div>

                <p className="text-sm text-foreground/90 leading-relaxed">{diagnosis.description}</p>

                {diagnosis.treatment && diagnosis.treatment.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recommended treatment</p>
                    <ul className="space-y-1.5">
                      {diagnosis.treatment.map((t, i) => (
                        <li key={i} className="flex gap-2 text-sm">
                          <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5 text-primary" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {diagnosis.timing && (
                  <div className="flex items-start gap-2 rounded-md bg-background/60 p-3 border border-border/50">
                    <Sparkle size={14} weight="fill" className="shrink-0 mt-0.5 text-primary" />
                    <p className="text-xs"><strong>Timing:</strong> {diagnosis.timing}</p>
                  </div>
                )}

                {diagnosis.prevention && (
                  <p className="text-xs text-muted-foreground"><strong>Prevention:</strong> {diagnosis.prevention}</p>
                )}

                {diagnosis.recommended_products?.length > 0 && (
                  <div className="pt-3 border-t border-primary/20">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Suggested products</p>
                    <div className="space-y-1.5">
                      {diagnosis.recommended_products.map((p, i) => (
                        <div key={i} className="flex items-center justify-between bg-background rounded-md px-3 py-2 border border-border/50" data-testid={`try-product-${p.id}`}>
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="text-sm font-medium truncate">{p.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">
                              {p.active_ingredient}{p.unit_size ? ` — ${p.unit_size}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {p.cost_per_unit && (
                              <span className="text-sm font-semibold text-primary">${p.cost_per_unit.toFixed(2)}</span>
                            )}
                            {p.affiliate_link && (
                              <a
                                href={p.affiliate_link}
                                target="_blank"
                                rel="nofollow sponsored noopener noreferrer"
                                className="text-[11px] px-2.5 py-1 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90"
                                data-testid={`try-buy-${p.id}`}
                              >
                                Buy
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    <div className="mb-2 rounded-md bg-muted/40 border border-border/60 px-3 py-2">
                      <AmazonDisclosure variant="inline" />
                    </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 italic">Price may vary — check retailer.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signup CTA */}
            <Card className="border-2 border-dashed border-primary/50 bg-gradient-to-br from-primary/10 to-transparent">
              <CardContent className="p-5 sm:p-7 text-center space-y-3">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider">
                  <Sparkle size={10} weight="fill" /> Next step
                </div>
                <h3 className="text-xl sm:text-2xl font-bold">Keep your lawn on the winning side.</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Create your TurfPulse account to track this issue through to resolution, get GDD-timed spray alerts, and unlock your personalized seasonal plan.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                  <Button onClick={() => navigate('/')} className="gap-2" data-testid="try-signup-btn">
                    Start my lawn plan <ArrowRight size={16} />
                  </Button>
                  <Button variant="outline" onClick={() => { setDiagnosis(null); setPhotoPreview(null); setPhotoBase64(null); setErrorMsg(''); }} data-testid="try-another-btn">
                    Try another photo
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground pt-1">
                  Includes: GDD-timed alerts · seasonal action plan · lawn-care rules · spray calculator
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* How it works */}
        {!diagnosis && (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { n: '01', title: 'Snap & upload', body: 'A clear photo of the trouble spot — sun or shade, close or wide.' },
              { n: '02', title: 'AI analyzes', body: 'Our AI identifies weeds, disease, pests, or stress in seconds.' },
              { n: '03', title: 'Get your fix', body: 'Step-by-step treatment plan with timing and product options.' },
            ].map((step) => (
              <div key={step.n} className="rounded-xl border border-border/60 bg-card p-5">
                <div className="text-primary text-xs font-bold tracking-widest mb-2">{step.n}</div>
                <h4 className="text-sm font-semibold mb-1">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-border/60 mt-16 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-[11px] text-muted-foreground">
          Powered by TurfPulse™ — Smart Lawn Care, Backed by Science
          <div className="flex items-center justify-center gap-3 mt-2">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TryDiagnosisPage;
